import React, { Component } from "react";
import { Form, Dropdown, OverlayTrigger, Tooltip } from "react-bootstrap";
import { PiEye, PiInfo } from "react-icons/pi";
import { toast } from "react-toastify";
import axios from "../../axios";
import axiosLib from "axios";
import apiError from "../../error";
import { getDate } from "../../function";
import { normalizeAttackType, attackVectorUsesLandingPage } from "../ai-components/campaign-template-utils";
import simulationandtrain from "../../assets/create_campaign/simulationandtrain.png";
import "../launch-modal/steps/steps.css";
import "./ai-template-selection.css";

const getCancelSource = () => {
	const CancelToken = axiosLib.CancelToken;
	return CancelToken ? CancelToken.source() : null;
};

/** Normalize API/parent values; avoids !!\"false\" === true. */
const coerceCredentialHarvesting = (raw) => {
	if (raw === true || raw === false) return raw;
	if (raw === 1 || raw === 0) return raw === 1;
	if (typeof raw === "string") {
		const s = String(raw).trim().toLowerCase();
		if (s === "true" || s === "1" || s === "yes" || s === "on") return true;
		if (s === "false" || s === "0" || s === "no" || s === "off" || s === "") return false;
	}
	return undefined;
};

const pickCredentialHarvestingRaw = (obj) => {
	if (!obj || typeof obj !== "object") return undefined;
	return obj.credential_harvesting ?? obj.credentialHarvesting;
};

/* A landing page is optional. `page.landing_url` is the flag; the backend normalises
   "none" to NULL on write, so truthiness is the whole test.
   Returns undefined when the endpoint doesn't report a page at all (currently System
   Templates — its list endpoint doesn't send this yet), so callers can tell "no
   landing page" apart from "don't know". */
const readLandingUrl = (t) => {
	if (!t || !Object.prototype.hasOwnProperty.call(t, 'page')) return undefined;
	return t.page?.landing_url || null;
};

/** Prefer full multi-select ids from parent; fallback to single selectedCategory (legacy). */
function categoryIdsFromProps(props) {
	if (Array.isArray(props.selectedCategoryIds) && props.selectedCategoryIds.length > 0) {
		return [...props.selectedCategoryIds];
	}
	if (props.selectedCategory != null && props.selectedCategory !== "") {
		return [props.selectedCategory];
	}
	return [];
}


export default class AiTemplateSelection extends Component {
	constructor(props) {
		super(props);
		this.categoriesCancel = null;
		this.templatesCancel = null;
		this.simulationCancel = null;
		const initialCatIds = categoryIdsFromProps(props);
		this.state = {
			loading: true,
			categories: [],
			selectedCategory: initialCatIds[0] || "",
			selectedCategoryIds: initialCatIds,
			// Distinguishes "no filter / show everything" (selectedCategoryIds: [] by default) from the user
			// explicitly unchecking every category (selectedCategoryIds: [] too, but should show zero templates).
			categoryFilterNone: false,
			templates: [],
			templatePath: "",
			templateMode: props.templateMode || "single",
			selectedTemplateId: props.templateId || null,
			selectedTemplateIds: Array.isArray(props.selectedTemplateIds) ? props.selectedTemplateIds : [],
			templateSearchInput: "",
			templateType: props.templateType === "System Templates" ? "System Templates" : "My Templates",
			// Attack-type id is required to filter store (system) templates by the chosen attack vector.
			storeAttackTypeId: null,
			// Fetched from simulation when props are empty (follow-up flow)
			fetchedAttackType: "",
			fetchedSimulationType: "",
			fetchedCredentialHarvesting: false,
			fetchedTargetInteractionType: "",
			fetchedIsAIbased: false,
		};
	}

	isSystemType = () => this.state.templateType === "System Templates";

	getAttackType = () => {
		const attackType = this.props.attackType || this.state.fetchedAttackType;
		return attackType ? normalizeAttackType(attackType) : attackType;
	};
	getSimulationType = () => this.props.simulationType || this.state.fetchedSimulationType;
	getCredentialHarvesting = () => {
		const cd = this.props.campaignDetails?.credentialHarvesting;
		if (cd !== undefined && cd !== null) {
			return coerceCredentialHarvesting(cd) ?? false;
		}
		const p = this.props.credentialHarvesting;
		if (p !== undefined && p !== null) {
			return coerceCredentialHarvesting(p) ?? false;
		}
		return coerceCredentialHarvesting(this.state.fetchedCredentialHarvesting) ?? false;
	};
	/** Prefer campaign details when set; blank string is treated as unset. For mode-based vectors, derive from isAIbased when tit is missing. */
	getTargetInteractionType = () => {
		if(this.props.targetInteractionType) return this.props.targetInteractionType;
		const cd = this.props.campaignDetails;
		const fromCd = cd && cd.targetInteractionType;
		if (fromCd !== undefined && fromCd !== null) {
			const s = String(fromCd).trim();
			if (s) return s;
		}
		if (this.getAttackType() === "Vishing" || this.getAttackType() === "WhatsApp") {
			return this.getIsAIbased() ? "inbound" : "outbound";
		}
		const fetched = this.state.fetchedTargetInteractionType;
		if (fetched !== undefined && fetched !== null && String(fetched).trim() !== "") {
			return String(fetched).trim();
		}
		return "";
	};
	/* Whether a landing page has any say over credential harvesting for this attack
	   vector. Inbound Vishing / AI WhatsApp harvest inside the call or chat and never
	   route through a landing page, so a template without one is perfectly usable --
	   only the vectors that do use a landing page may be blocked on a missing one. */
	usesLandingPage = () => attackVectorUsesLandingPage({
		attackType: this.getAttackType(),
		targetInteractionType: this.getTargetInteractionType(),
		isAIbased: this.getIsAIbased()
	});
	/** Prefer campaign details when boolean is defined; avoid !== null only (undefined was wrongly treated as “use details”). */
	getIsAIbased = () => {
		if(this.props.isAIbased) return this.props.isAIbased;
		const cd = this.props.campaignDetails;
		if (cd && cd.isAIbased !== undefined && cd.isAIbased !== null) {
			return !!cd.isAIbased;
		}
		return !!this.state.fetchedIsAIbased;
	};

	componentDidMount() {
		if (this.props.campaignId && this.props.simulationId) {
			this.fetchCampaignContext();
		} else {
			this.loadCategories();
		}
	}

	componentWillUnmount() {
		if (this.categoriesCancel) this.categoriesCancel();
		if (this.templatesCancel) this.templatesCancel();
		if (this.simulationCancel) this.simulationCancel();
	}

	fetchCampaignContext = () => {
		const { campaignId, simulationId } = this.props;
		if (!campaignId || !simulationId) return;
		if (this.simulationCancel) this.simulationCancel();
		const source = getCancelSource();
		if (source) this.simulationCancel = source.cancel;
		axios.get("/api/simulation/", {
			params: { campaignId, simulationId },
			cancelToken: source ? source.token : undefined
		})
			.then((res) => {
				if (res.data?.simulation?.campaign) {
					const sim = res.data.simulation;
					const camp = sim.campaign;
					const attackType = camp.attack_type?.name || "";
					const simulationType = (camp.simulation_type?.name || "").trim();
					const chRaw =
						pickCredentialHarvestingRaw(camp) ?? pickCredentialHarvestingRaw(sim);
					const credentialHarvesting = coerceCredentialHarvesting(chRaw) ?? false;
					const targetInteractionType = camp.targetInteractionType || sim.targetInteractionType || "";
					const fetchedIsAIbased = !!(camp.isAIbased ?? sim.isAIbased);
					this.setState(
						{
							fetchedAttackType: attackType,
							fetchedSimulationType: simulationType,
							fetchedCredentialHarvesting: credentialHarvesting,
							fetchedTargetInteractionType: targetInteractionType,
							fetchedIsAIbased,
						},
						() => this.loadCategories()
					);
				} else {
					this.loadCategories();
				}
			})
			.catch((err) => {
				if (!axiosLib.isCancel(err)) this.setState({ loading: false });
				this.loadCategories();
			});
	};

	componentDidUpdate(prevProps) {
		console.log("the this .props inside ai template selection: ", this.props);
		// Sync template source when the parent changes it (e.g. navigating back to this step).
		if (prevProps.templateType !== this.props.templateType) {
			const nextType = this.props.templateType === "System Templates" ? "System Templates" : "My Templates";
			if (nextType !== this.state.templateType) {
				this.setState({ templateType: nextType, storeAttackTypeId: null }, () => this.loadCategories());
				return;
			}
		}
		// Load when filter props change
		if (prevProps.simulationType !== this.props.simulationType ||
			prevProps.attackType !== this.props.attackType ||
			prevProps.credentialHarvesting !== this.props.credentialHarvesting ||
			prevProps.campaignDetails?.credentialHarvesting !== this.props.campaignDetails?.credentialHarvesting) {
			this.loadCategories();
			return;
		}
		if (prevProps.campaignDetails?.targetInteractionType !== this.props.campaignDetails?.targetInteractionType ||
			prevProps.campaignDetails?.isAIbased !== this.props.campaignDetails?.isAIbased) {
			this.loadCategories();
			return;
		}
		// Load when attackType or simulationType becomes available (e.g. async from parent) and we haven't loaded yet
		const filterNowAvailable = (!prevProps.attackType && this.props.attackType) || (!prevProps.simulationType && this.props.simulationType);
		if (filterNowAvailable && this.state.categories.length === 0 && !this.state.loading) {
			this.loadCategories();
		}
		const prevCamp = prevProps.campaignId;
		const nextCamp = this.props.campaignId;
		const prevSim = prevProps.simulationId;
		const nextSim = this.props.simulationId;
		if ((prevCamp !== nextCamp || prevSim !== nextSim) && nextCamp && nextSim) {
			this.fetchCampaignContext();
		}

		if (prevProps.templateId !== this.props.templateId) {
			this.setState({ selectedTemplateId: this.props.templateId || null });
		}
		// Sync selection from parent when user navigates back (e.g. from Preview) so step reflects current state
		const prevIds = Array.isArray(prevProps.selectedTemplateIds) ? prevProps.selectedTemplateIds : [];
		const nextIds = Array.isArray(this.props.selectedTemplateIds) ? this.props.selectedTemplateIds : [];
		const idsEqual = prevIds.length === nextIds.length && nextIds.every((id, i) => id === prevIds[i]);
		if (!idsEqual || prevProps.templateId !== this.props.templateId) {
			this.setState({
				selectedTemplateId: this.props.templateId || null,
				selectedTemplateIds: nextIds.length ? [...nextIds] : []
			});
		}
		// Sync category filters when parent updates (e.g. navigate back to this step). Parent keeps full selectedCategoryIds array.
		const prevCatIds = categoryIdsFromProps(prevProps);
		const nextCatIds = categoryIdsFromProps(this.props);
		const categoryPropChanged =
			prevCatIds.length !== nextCatIds.length ||
			nextCatIds.some((id, i) => id !== prevCatIds[i]);
		const templateModePropChanged = prevProps.templateMode !== this.props.templateMode;
		if (categoryPropChanged || templateModePropChanged) {
			if (categoryPropChanged) {
				const ids = nextCatIds;
				const newCategory = ids[0] || "";
				this.setState({
					selectedCategory: newCategory,
					selectedCategoryIds: ids,
					categoryFilterNone: false,
					templateMode: this.props.templateMode || "single",
					selectedTemplateIds: Array.isArray(this.props.selectedTemplateIds) ? this.props.selectedTemplateIds : []
				}, () => this.loadTemplates());
			} else if (templateModePropChanged) {
				this.setState({
					templateMode: this.props.templateMode || "single",
					selectedTemplateIds: Array.isArray(this.props.selectedTemplateIds) ? this.props.selectedTemplateIds : []
				});
			}
		}
	}
	loadTemplates = (categoryIdsOverride, options = {}) => {
		// User explicitly deselected every category ("All" unchecked with nothing else checked) — show
		// zero templates without hitting the API. A locked/prefilled (readOnly) template is unaffected.
		if (this.state.categoryFilterNone && categoryIdsOverride === undefined && !this.props.readOnly) {
			if (this.templatesCancel) {
				this.templatesCancel();
				this.templatesCancel = null;
			}
			this.setState({ loading: false, templates: [] }, () => {
				if (!options.preserveSelection) this.updateData();
			});
			return;
		}
		const ids = categoryIdsOverride !== undefined ? (Array.isArray(categoryIdsOverride) ? categoryIdsOverride : [categoryIdsOverride]) : this.state.selectedCategoryIds;
		const search = (this.state.templateSearchInput || "").trim();
		const isSystem = this.isSystemType();

		if (this.templatesCancel) {
			this.templatesCancel();
			this.templatesCancel = null;
		}
		const source = getCancelSource();
		if (source) this.templatesCancel = source.cancel;
		this.setState({ loading: true });
		const url = isSystem ? '/api/store/fetch-store-system-templates' : '/api/campaign/template/template';
		const params = isSystem
			? {
				type: "System Templates",
				// Unlike My Templates, the store endpoint actually filters by this — but credential
				// harvesting is a campaign-level setting now, not a template-level one (My Templates
				// already ignores it), so System should list every template regardless of the toggle too.
				targetInteractionType: this.getTargetInteractionType(),
				isAIbased: this.getIsAIbased(),
				...(this.state.storeAttackTypeId != null ? { attackTypeIds: [this.state.storeAttackTypeId] } : {})
			}
			: {
				type: "My Templates",
				attackType: this.getAttackType() ?? "",
				credentialHarvesting: this.getCredentialHarvesting(),
				simulationType: this.getSimulationType() ?? "",
				targetInteractionType: this.getTargetInteractionType(),
				isAIbased: this.getIsAIbased()
			};
		if (ids && ids.length > 0) params.categoryIds = ids;
		if (search) params.search = search;
		axios.get(url, {
			params,
			cancelToken: source ? source.token : undefined
		})
			.then((res) => {
				let templates = res.data.campaign_template || res.data.campaign_templates || [];
				const templatePath = res.data.path || "";
				// Store templates only send a thumbnail filename + base path; build the URL the card expects.
				if (isSystem && templatePath) {
					templates = templates.map((t) => {
						if (t && t.template && t.template.thumbnail && !t.template.thumbnailUrl) {
							return { ...t, template: { ...t.template, thumbnailUrl: templatePath + t.template.thumbnail } };
						}
						return t;
					});
				}
				// Launch from template preview / edit: show only the current template, not the full category list
				if (this.props.readOnly) {
					const raw =
						this.props.campaignTemplateId != null && this.props.campaignTemplateId !== ""
							? this.props.campaignTemplateId
							: this.props.templateId;
					const lockId = raw != null && raw !== "" ? parseInt(raw, 10) : NaN;
					if (!Number.isNaN(lockId)) {
						const matched = templates.filter((t) => t.id === lockId);
						if (matched.length > 0) {
							templates = matched;
						}
					}
				}
				this.setState({
					loading: false,
					templates,
					templatePath,
					...(!options.preserveSelection && templates.length <= 1 && this.state.templateMode === "random"
						? { templateMode: "single", selectedTemplateIds: [] }
						: {})
				}, () => {
					// Refresh parent selectedTemplates after templates load so Preview stays in sync
					if (!options.preserveSelection) this.updateData();
				});
			})
			.catch((error) => {
				if (!axiosLib.isCancel(error)) {
					this.setState({ loading: false, templates: [] });
					apiError(error);
				}
			});
	};

	loadCategories = () => {
		const attackType = this.getAttackType();
		const simulationType = this.getSimulationType();
		if (!attackType && !simulationType) return;

		if (this.isSystemType()) {
			this.loadSystemCategories();
			return;
		}

		if (this.categoriesCancel) {
			this.categoriesCancel();
			this.categoriesCancel = null;
		}
		const source = getCancelSource();
		if (source) this.categoriesCancel = source.cancel;
		this.setState({ loading: true });
		axios.get('/api/campaign/template/categories', {
			params: {
				type: "My Templates",
				attackType: attackType ?? "",
				credentialHarvesting: this.getCredentialHarvesting(),
				simulationType: simulationType ?? "",
				targetInteractionType: this.getTargetInteractionType(),
				isAIbased: this.getIsAIbased()
			},
			cancelToken: source ? source.token : undefined
		})
			.then((res) => {
				const categories = res.data.template_category || [];
				this.setState({ loading: false, categories }, () => {
					const ids = categoryIdsFromProps(this.props);
					this.setState({ selectedCategoryIds: ids, selectedCategory: ids[0] || "", categoryFilterNone: false }, () => this.loadTemplates());
				});
			})
			.catch((error) => {
				if (!axiosLib.isCancel(error)) {
					this.setState({ loading: false, categories: [] });
					apiError(error);
				}
			});
	};

	/** System (store) templates: resolve the chosen attack-type id, then load store categories + templates. */
	loadSystemCategories = () => {
		if (this.categoriesCancel) {
			this.categoriesCancel();
			this.categoriesCancel = null;
		}
		const source = getCancelSource();
		if (source) this.categoriesCancel = source.cancel;
		this.setState({ loading: true });
		this.resolveStoreAttackTypeId()
			.then(() => axios.get('/api/store/fetch-categories', { cancelToken: source ? source.token : undefined }))
			.then((res) => {
				const raw = res.data.response || [];
				// Normalize to the shape the category dropdown renders ({ category: { id, name } }).
				const categories = raw.map((c) => ({ category: { id: c.id, name: c.name } }));
				this.setState({ loading: false, categories }, () => {
					const ids = categoryIdsFromProps(this.props);
					this.setState({ selectedCategoryIds: ids, selectedCategory: ids[0] || "", categoryFilterNone: false }, () => this.loadTemplates());
				});
			})
			.catch((error) => {
				if (!axiosLib.isCancel(error)) {
					this.setState({ loading: false, categories: [] });
					apiError(error);
				}
			});
	};

	/** Map the chosen attack-type name to its id so store templates can be filtered by attack vector. */
	resolveStoreAttackTypeId = () => {
		const attackType = this.getAttackType();
		if (!attackType) return Promise.resolve(null);
		return axios.get('/api/attacktypes', {})
			.then((res) => {
				const list = res.data.attackTypes || [];
				const match = list.find((a) => a.name === attackType);
				const id = match ? match.id : null;
				this.setState({ storeAttackTypeId: id });
				return id;
			})
			.catch(() => {
				this.setState({ storeAttackTypeId: null });
				return null;
			});
	};

	handleTemplateTypeChange = (value) => {
		const templateType = value === "System Templates" ? "System Templates" : "My Templates";
		if (templateType === this.state.templateType) return;
		// Switching source invalidates any current category/template selection.
		this.setState({
			templateType,
			categories: [],
			selectedCategory: "",
			selectedCategoryIds: [],
			selectedTemplateId: null,
			selectedTemplateIds: [],
			templateMode: "single",
			storeAttackTypeId: null,
			templateSearchInput: ""
		}, () => {
			this.updateData();
			this.loadCategories();
		});
	};

	handleCategoryChange = (e) => {
		const value = e.target.value;
		const ids = value === "" ? [] : [value];
		this.setState({
			selectedCategory: value,
			selectedCategoryIds: ids,
			categoryFilterNone: false,
			selectedTemplateId: null,
			selectedTemplateIds: []
		}, () => {
			this.loadTemplates();
			this.updateData();
		});
	};

	/** All category ids currently available in the filter list. */
	getAllCategoryIds = () => (this.state.categories || []).map((cat) => cat.category?.id || cat.id);

	/** True when every category checkbox currently reads as checked (implicit-all "no filter", or every id explicitly selected). */
	isEveryCategorySelected = () => {
		if (this.state.categoryFilterNone) return false;
		if (this.state.selectedCategoryIds.length === 0) return true;
		const allIds = this.getAllCategoryIds();
		// Compare as strings: category ids from the API (numbers) and ids stored from legacy
		// single-select props (strings) shouldn't silently fail `.includes` on a type mismatch.
		const selected = this.state.selectedCategoryIds.map(String);
		return allIds.length > 0 && allIds.every((id) => selected.includes(String(id)));
	};

	handleCategoryAllToggle = () => {
		const wasAllSelected = this.isEveryCategorySelected();
		// Toggling off "All" means the user explicitly wants zero categories (show nothing);
		// toggling it on selects every id explicitly so each item reads as checked.
		const newIds = wasAllSelected ? [] : this.getAllCategoryIds();
		this.setState({
			selectedCategoryIds: newIds,
			selectedCategory: newIds[0] || "",
			categoryFilterNone: wasAllSelected,
			selectedTemplateId: null,
			selectedTemplateIds: []
		}, () => {
			this.loadTemplates();
			this.updateData();
		});
	};

	handleCategoryToggle = (catId) => {
		// Implicit-all state ([] with categoryFilterNone false) expands to the explicit full list first,
		// so unchecking one item keeps the rest checked instead of collapsing to "only this one selected".
		const ids = (this.state.selectedCategoryIds.length === 0 && !this.state.categoryFilterNone)
			? this.getAllCategoryIds()
			: this.state.selectedCategoryIds;
		const isSelected = ids.some((id) => String(id) === String(catId));
		const newIds = isSelected ? ids.filter((id) => String(id) !== String(catId)) : [...ids, catId];
		this.setState({
			selectedCategoryIds: newIds,
			selectedCategory: newIds[0] || "",
			// Unchecking the last remaining category means "explicitly none"; checking any means it's no longer "none".
			categoryFilterNone: newIds.length === 0,
			selectedTemplateId: null,
			selectedTemplateIds: []
		}, () => {
			this.loadTemplates();
			this.updateData();
		});
	};

	handleTemplateModeChange = (mode) => {
		const canUseMultiple = (this.state.templates || []).length > 1;
		if (mode === "random" && !canUseMultiple) return;
		this.setState({
			templateMode: mode,
			selectedTemplateId: null,
			selectedTemplateIds: []
		}, () => this.updateData());
	};

	handleTemplateSelect = (templateId) => {
		if (this.state.templateMode === "single") {
			this.setState({ selectedTemplateId: templateId }, () => this.updateData());
		} else {
			const currentIds = this.state.selectedTemplateIds || [];
			const isSelected = currentIds.includes(templateId);
			const newIds = isSelected
				? currentIds.filter(id => id !== templateId)
				: [...currentIds, templateId];
			this.setState({ selectedTemplateIds: newIds }, () => this.updateData());
		}
	};

	handleTemplateSearchChange = (e) => {
		const value = e.target.value;
		this.setState({ templateSearchInput: value }, () => {
			this.loadTemplates(undefined, { preserveSelection: true });
		});
	};

	/** Builds the list of selected templates from current state (id + display info). */
	getSelectedTemplates = () => {
		const { templateMode, selectedTemplateId, selectedTemplateIds, templates, templatePath } = this.state;
		let selectedTemplates = [];
		if (templateMode === "single" && selectedTemplateId) {
			const t = templates.find((tpl) => tpl.id === selectedTemplateId);
			if (t) {
				selectedTemplates = [
					{
						id: t.id,
						name: t.name,
						thumbnail: t.template?.thumbnail,
						thumbnailUrl: t.template?.thumbnailUrl,
						templatePath: templatePath
					}
				];
			}
		} else if (templateMode === "random" && selectedTemplateIds.length > 0) {
			selectedTemplates = selectedTemplateIds.map((id) => {
				const t = templates.find((tpl) => tpl.id === id);
				return t
					? {
							id: t.id,
							name: t.name,
							thumbnail: t.template?.thumbnail,
							thumbnailUrl: t.template?.thumbnailUrl,
							templatePath: templatePath
						}
					: { id, name: "Template" };
			});
		}
		return selectedTemplates;
	};

	updateData = () => {
		if (!this.props.onUpdate) return;
		let { templateMode, selectedTemplateId, selectedTemplateIds, templates } = this.state;

		// After filters/categories reload, drop ids that are no longer in the list so preview/parent stay in sync.
		if (templates.length > 0) {
			if (templateMode === "single" && selectedTemplateId != null && !templates.some((tpl) => tpl.id === selectedTemplateId)) {
				this.setState({ selectedTemplateId: null }, () => this.updateData());
				return;
			}
			if (templateMode === "random" && selectedTemplateIds.length > 0) {
				const nextIds = selectedTemplateIds.filter((id) => templates.some((tpl) => tpl.id === id));
				if (nextIds.length !== selectedTemplateIds.length) {
					this.setState({ selectedTemplateIds: nextIds }, () => this.updateData());
					return;
				}
			}
		}

		const idsForParent =
			templateMode === "single"
				? selectedTemplateId != null && selectedTemplateId !== ""
					? [selectedTemplateId]
					: []
				: selectedTemplateIds;

		const templateId =
			templateMode === "single"
				? selectedTemplateId
				: selectedTemplateIds.length > 0
					? selectedTemplateIds[0]
					: null;

		this.props.onUpdate({
			templateId,
			templateType: this.state.templateType,
			selectedCategory: this.state.selectedCategory,
			selectedCategoryIds: this.state.selectedCategoryIds,
			selectedTemplateIds: idsForParent,
			templateMode,
			selectedTemplates: this.getSelectedTemplates()
		});
	};

	validate = () => {
		const canUseMultiple = (this.state.templates || []).length > 1;
		if (this.state.templateMode === "single") {
			const hasSelection = this.state.selectedTemplateId != null && this.state.selectedTemplateId !== "";
			if (!hasSelection) {
				toast.error("Please select a template");
				return false;
			}
		} else {
			if (!canUseMultiple) {
				toast.error("Multiple mode is available only when more than one template is available");
				return false;
			}
			if (!this.state.selectedTemplateIds || this.state.selectedTemplateIds.length < 2) {
				toast.error("Please select at least two templates");
				return false;
			}
		}
		return true;
	};

	handleDone = async () => {
		if (!this.validate()) return;
		if (!this.props.onDone) return;

		const selected = this.getSelectedTemplates();
		if (!this.isSystemType()) {
			this.props.onDone(selected);
			return;
		}

		this.setState({ loading: true });
		try {
			const cloned = await Promise.all(
				selected.map((tpl) =>
					axios.post(`/api/store/clone-campaign-template/${tpl.id}`, {}).then((res) => ({
						...tpl,
						id: res.data.campaignTemplateId ?? res.data.response?.id,
						status: res.data.status
					}))
				)
			);

			if (cloned.some((t) => t.id == null)) {
				toast.error("Failed to clone the selected system template");
				return;
			}
			/* A clone can land as a draft (missing sender, landing page, …). Running the campaign
			   against it would fail later, so stop here and point the user at the editor. */
			const draft = cloned.find((t) => String(t.status || "").toLowerCase() === "draft");
			if (draft) {
				toast.error(`"${draft.name}" was cloned as a draft. Open it in Templates, complete and save it, then select it from My Templates.`);
				return;
			}

			this.props.onDone(cloned.map(({ status, ...tpl }) => tpl));
		} catch (error) {
			apiError(error);
		} finally {
			this.setState({ loading: false });
		}
	};

	handleCreateTemplate = () => {
		if (!this.props.onCreateTemplate) return;
		// Pass the resolved attack-type / credential-harvesting context and current
		// selection so the template editor renders the correct surfaces.
		this.props.onCreateTemplate({
			attackType: this.getAttackType(),
			simulationType: this.getSimulationType(),
			credentialHarvesting: this.getCredentialHarvesting(),
			targetInteractionType: this.getTargetInteractionType(),
			isAIbased: this.getIsAIbased(),
		});
	};

	formatDate = (dateString) => {
		if (!dateString) return "";
		return getDate(dateString);
	};

	render() {
		const { categories, selectedCategoryIds, categoryFilterNone, templates, loading, templateMode, selectedTemplateId, selectedTemplateIds, templateSearchInput, templateType } = this.state;
		const { readOnly } = this.props;
		const canUseMultiple = (templates || []).length > 1;
		const categoryLabel = categoryFilterNone
			? "None"
			: this.isEveryCategorySelected()
				? "All"
				: String(selectedCategoryIds.length);

		return (
			<div className="step-content-wrapper ai-template-selection">
				{
					!readOnly && (
						<div className={`template-selection-filters ${readOnly ? "read-only" : ""}`} style={readOnly ? { pointerEvents: "none", opacity: 0.9 } : {}}>
					<div className="ai-filter-wrap"><div className="filter-row">
						<Form.Group className="filter-group type-filter-group">
							<Form.Label className="filter-label">Type</Form.Label>
							<Dropdown className="template-type-filter-dropdown">
								<Dropdown.Toggle variant="secondary" id="template-type-filter-dropdown" className="filter-select template-type-dropdown-toggle">
									{templateType === "System Templates" ? "System" : "My Templates"}
								</Dropdown.Toggle>
								<Dropdown.Menu>
									<Dropdown.Item
										active={templateType === "System Templates"}
										onClick={() => this.handleTemplateTypeChange("System Templates")}
									>
										System
									</Dropdown.Item>
									<Dropdown.Item
										active={templateType === "My Templates"}
										onClick={() => this.handleTemplateTypeChange("My Templates")}
									>
										My Templates
									</Dropdown.Item>
								</Dropdown.Menu>
							</Dropdown>
						</Form.Group>
						<div className="filter-separator" />
						<Form.Group className="filter-group category-filter-group">
							<Form.Label className="filter-label">Filter By</Form.Label>
							<Dropdown className="category-filter-dropdown">
								<Dropdown.Toggle variant="secondary" id="category-filter-dropdown" className="filter-select category-dropdown-toggle">
									Category: {categoryLabel}
								</Dropdown.Toggle>
								<Dropdown.Menu className="category-multiselect-menu">
									{/* No `label` prop on Form.Check below — a real <label for> associated with the
									    input natively forwards a second click to it, which was causing one user
									    click to register as two toggles that canceled out. Rendering the text as a
									    plain <span> (not a <label>) removes that mechanism entirely, so this
									    wrapper's onClick is the only thing that can ever fire the toggle. */}
									<Dropdown.Item
										as="div"
										className="category-filter-item"
										onClick={(e) => { e.preventDefault(); this.handleCategoryAllToggle(); }}
									>
										<div
											className="category-filter-checkbox-wrap"
											onClick={(e) => { e.preventDefault(); e.stopPropagation(); this.handleCategoryAllToggle(); }}
										>
											<Form.Check
												type="checkbox"
												id="category-filter-all"
												checked={this.isEveryCategorySelected()}
												onChange={() => {}}
												readOnly
											/>
											<span className="form-check-label">All</span>
										</div>
									</Dropdown.Item>
									{categories.map((cat, index) => {
										const catId = cat.category?.id || cat.id;
										const catName = cat.category?.name || cat.name || "";
										const label = cat.count != null ? `${catName} (${cat.count} Templates)` : catName;
										const isChecked = !categoryFilterNone && (
											selectedCategoryIds.length === 0 ||
											selectedCategoryIds.some((id) => String(id) === String(catId))
										);
										return (
											<Dropdown.Item
												key={catId}
												as="div"
												className="category-filter-item"
												onClick={(e) => { e.preventDefault(); this.handleCategoryToggle(catId); }}
											>
												<div
													className="category-filter-checkbox-wrap"
													onClick={(e) => { e.preventDefault(); e.stopPropagation(); this.handleCategoryToggle(catId); }}
												>
													<Form.Check
														type="checkbox"
														id={`category-filter-${catId}`}
														checked={isChecked}
														onChange={() => {}}
														readOnly
													/>
													<span className="form-check-label">{label}</span>
												</div>
											</Dropdown.Item>
										);
									})}
								</Dropdown.Menu>
							</Dropdown>
						</Form.Group>
						<div className="filter-separator" />
						<Form.Group className="filter-group template-mode-filter-group">
							<Form.Label className="template-mode-label">Template Mode</Form.Label>
							<div className="template-mode-options">
								<div
									className={`template-mode-option ${templateMode === "single" ? "selected" : ""}`}
									onClick={() => this.handleTemplateModeChange("single")}
								>
									<Form.Check
										type="radio"
										name="templateMode"
										onChange={() => this.handleTemplateModeChange("single")}
										label={
											<span className="template-mode-label-wrap">
												Single
												<OverlayTrigger
													placement="top"
													overlay={<Tooltip id="single-template-mode-tip">Uses one template for all users in the campaign.</Tooltip>}
												>
													<span className="template-mode-info-icon" onClick={(e) => e.stopPropagation()}>
														<PiInfo />
													</span>
												</OverlayTrigger>
											</span>
										}
										checked={templateMode === "single"}
									/>
								</div>
								<div className="filter-separator-in" />
								<div
									className={`template-mode-option ${templateMode === "random" ? "selected" : ""} ${!canUseMultiple ? "disabled" : ""}`}
									onClick={() => canUseMultiple && this.handleTemplateModeChange("random")}
								>
									<Form.Check
										type="radio"
										name="templateMode"
										onChange={() => this.handleTemplateModeChange("random")}
										label={
											<span className="template-mode-label-wrap">
												Random
												<OverlayTrigger
													placement="top"
													overlay={<Tooltip id="multiple-template-mode-tip">Distributes multiple selected templates randomly among users.</Tooltip>}
												>
													<span className="template-mode-info-icon" onClick={(e) => e.stopPropagation()}>
														<PiInfo />
													</span>
												</OverlayTrigger>
											</span>
										}
										checked={templateMode === "random"}
										disabled={!canUseMultiple}
									/>
								</div>
							</div>
						</Form.Group>
						<Form.Group className="template-search-group ms-2">
							<Form.Control
								type="search"
								className="template-search-input"
								placeholder="Search"
								value={templateSearchInput}
								onChange={this.handleTemplateSearchChange}
								aria-label="Search templates"
							/>
						</Form.Group>
					</div>
				</div></div>

					)
				}

				{loading ? (
					<div className="template-loading">Loading templates...</div>
				) : (
					<div className="template-grid">
						{templates.length > 0 ? (
							templates.map((template, index) => {
								const isSelected = templateMode === "single"
									? selectedTemplateId === template.id
									: selectedTemplateIds.includes(template.id);

								const thumbnailSrc = template.template?.thumbnailUrl
									? template.template.thumbnailUrl
									: simulationandtrain;

								/* Where harvesting does happen on a landing page, a template without one can't
								   be used for this campaign, so block selecting it instead of letting the user
								   pick it and only warning afterwards. The vectors that harvest in the call/chat
								   itself never hit this — see usesLandingPage. System Templates report the page
								   too now (fetchSystemTemplatesFromStore maps the store's landing_page onto
								   `page`); readLandingUrl still returns undefined if the store omits the
								   association, and an undefined never disables a card. */
								const templateLandingUrl = readLandingUrl(template);
								const noLandingPage = this.getCredentialHarvesting() && this.usesLandingPage() &&
									templateLandingUrl !== undefined && !templateLandingUrl;
								const isDisabled = readOnly || noLandingPage;

								const card = (
									<div
										key={index}
										className={`template-card ${isSelected ? "selected" : ""} ${isDisabled ? "read-only" : ""}`}
										onClick={() => !isDisabled && this.handleTemplateSelect(template.id)}
										 style={{
											filter: isDisabled ? "grayscale(70%)" : "none",
											// Only readOnly (a preview with no interactions at all) goes pointer-events:none —
											// a no-landing-page card must stay hoverable so its tooltip can still show.
											pointerEvents: readOnly ? "none" : "auto",
											opacity: isDisabled ? 0.85 : 1,
											cursor: noLandingPage ? "not-allowed" : undefined,
										}}
									>
										<div className="template-card-image">
											<img
												src={thumbnailSrc}
												alt={template.name || "Template thumbnail"}
												className="img-fluid"
											/>
											{template.simulation_type && (
												<div className="template-simulation-badge">
													{template.simulation_type.name}
												</div>
											)}
											{templateMode === "random" && (
												<div className="template-card-checkbox-wrapper">
													<Form.Check
														type="checkbox"
														checked={isSelected}
														disabled={isDisabled}
														onChange={(e) => {
															e.stopPropagation();
															if (!isDisabled) this.handleTemplateSelect(template.id);
														}}
														onClick={(e) => e.stopPropagation()}
														className="template-card-checkbox-input"
													/>
												</div>
											)}
										</div>
										<div className="template-card-content">
											<div className="template-card-title-row">
												<h6 className="template-card-title">{template.name}</h6>
												<PiEye
													className="template-view-icon"
													onClick={(e) => {
														e.stopPropagation();
														if (this.props.onPreviewTemplate) this.props.onPreviewTemplate(template.id, this.isSystemType());
													}}
												/>
											</div>
											<p className="template-card-date">{this.formatDate(template.createdAt)}</p>
											<div className="template-card-tags">
												{template.attack_type && (
													<span className="template-tag-attack">{template.attack_type.name}</span>
												)}
												{template.simulation_type && (
													<span className="template-tag-category">{template.simulation_type.name}</span>
												)}
											</div>
										</div>
									</div>
								);

								if (!noLandingPage) return card;

								// Attach directly to the card div (no extra wrapper) — a wrapping span
								// here would sit in the grid as its own item and, unlike every other
								// card, size to its content instead of the grid track's width, which
								// visibly resized the card on hover.
								return (
									<OverlayTrigger
										key={index}
										placement="top"
										overlay={<Tooltip id={`no-landing-page-${template.id}`}>No landing page</Tooltip>}
									>
										{card}
									</OverlayTrigger>
								);
							})
						) : (
							templateSearchInput.trim() ? (
								<div className="template-empty">No templates found</div>
							) : selectedCategoryIds.length > 0 ? (
								<div className="template-empty">No templates found in this category.</div>
							) : (
								<div className="template-empty">Please select a category to view templates.</div>
							)
						)}
					</div>
				)}

				{!readOnly && (
					<div className="ai-template-footer">
						<button
							type="button"
							className="ai-template-done-btn"
							onClick={this.handleDone}
						>
							✓ Done
						</button>
						<button
							type="button"
							className="ai-template-create-btn"
							onClick={this.handleCreateTemplate}
						>
							+ Create Template
						</button>
					</div>
				)}
			</div>
		);
	}
}
